import React, { useMemo } from 'react';
import { generateFee, FeeType } from '@ourbranch/fee-utils';
import { policyTypes } from '@ourbranch/policy-types';
import { standaloneRentersStates, statesEligibleForBranchPlusMPD } from '@ourbranch/lookups';

import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import {
  canPersonBeFullTimeStudent,
  canPersonReceiveDrivingCourseDiscount,
  canReceiveCommunityDriveDiscount
} from 'common/components/people/helpers';
import { getCoveragesDiscount, summedPolicyDiscountIDs, getOptionIfNotBundle } from '../helpers/get-coverages-discount';

export const useDiscounts = () => {
  const {
    offer: store,
    offer: { offer }
  } = useStore();
  const { selectedOption, allowPaperlessDiscount, allowInventoryDiscount, includeBranchPlusMultiPolicyDiscount } =
    store;

  const { isAgency } = useSession();

  return useMemo(() => {
    if (!offer || !selectedOption) {
      return [];
    }

    const discounts = [];

    const bundleOption = offer.options?.find((o) => o.name === 'Bundle');
    const autoOption = offer.options?.find((o) => o.type === policyTypes.Auto);
    const homeOption = offer.options?.find((o) => o.type === policyTypes.Home);
    const rentersOption = offer.options?.find((o) => o.type === policyTypes.Renters);
    const condoOption = offer.options?.find((o) => o.type === policyTypes.Condo);

    // for use when policy is not a bundle, but more than one policy type has the same discount ID
    const optionForDiscountCheck = getOptionIfNotBundle(
      selectedOption,
      { autoOption, homeOption, rentersOption, condoOption },
      store
    );

    const { affinity, discountInventoryScore, discountPaperless } = offer.quote?.global || {};

    const isBundle =
      [policyTypes.ARBundle, policyTypes.HABundle, policyTypes.CABundle, 'AB', 'HB'].includes(selectedOption) ||
      (offer.quote.forcedMultiPolicyDiscount && offer.quote.forcedMultiPolicyDiscount !== 'N');

    const hasRenters = offer.options?.some(
      (opt) => opt.type === policyTypes.ARBundle || opt.type === policyTypes.Renters
    );

    const multiPolicyAuto = autoOption?.autoCoverages?.find((cov) => cov.type === 'multi_policy_savings');
    const multiPolicyHome = homeOption?.homeCoverages?.find((cov) => cov.type === 'multi_policy_savings');
    const multiPolicyCondo = condoOption?.condoCoverages?.find((cov) => cov.type === 'multi_policy_savings');
    const multiPolicyAvailable =
      multiPolicyAuto?.amount > 0 || multiPolicyHome?.amount > 0 || multiPolicyCondo?.amount > 0;
    const bundleOptionAvailable = offer.options?.some((o) => o.name === 'Bundle');

    // MARK: Bundle Discount
    const calculateBundleSavings = () => {
      const { existingCustomer, noBindAuto, noBindHome } = offer.quote;
      if (offer) {
        let homeSavings = 0;
        let autoSavings = 0;
        let condoSavings = 0;

        // If the customer has a bundle and a multiline discount, we want to display both savings combined in the bundle savings component
        // If they only have a bundle, but not multiline discount, we will only display bundle savings
        // If they don't have the bundle option, but they do have a multiline discount, we display the multiline discount savings in the bundle savings component

        // if already have a home policy we don't apply the discount
        if (!(existingCustomer && noBindHome && hasRenters) && homeOption?.homeTotal) {
          if (bundleOption?.homeTotal) {
            homeSavings = multiPolicyAvailable
              ? homeOption?.homeTotal - bundleOption?.homeTotal + multiPolicyHome?.amount
              : homeOption?.homeTotal - bundleOption?.homeTotal;
          } else {
            homeSavings = multiPolicyHome?.amount;
          }
        }

        if (condoOption?.condoTotal) {
          if (bundleOption?.condoTotal) {
            condoSavings = multiPolicyAvailable
              ? condoOption.condoTotal - bundleOption.condoTotal + multiPolicyCondo?.amount
              : condoOption.condoTotal - bundleOption.condoTotal;
          } else {
            condoSavings = multiPolicyCondo?.amount;
          }
        }

        // if already have an auto policy we don't apply the discount
        if (!(existingCustomer && noBindAuto) && autoOption?.autoPremium) {
          if (bundleOption?.autoPremium) {
            const rentersTotal =
              bundleOption.autoCoverages?.find((coverage) => coverage.type === 'renters_total')?.amount || 0;
            autoSavings = multiPolicyAvailable
              ? (autoOption.autoPremium - (bundleOption.autoPremium - rentersTotal)) * 2 + multiPolicyAuto?.amount
              : (autoOption.autoPremium - (bundleOption.autoPremium - rentersTotal)) * 2;
          } else {
            autoSavings = multiPolicyAuto?.amount;
          }
        }

        switch (selectedOption) {
          case 'AB':
            return autoSavings;
          case 'HB':
            return homeSavings;
          default:
            return autoSavings + homeSavings + condoSavings;
        }
      }
      return null;
    };

    const bundleSavings = {
      label: 'Bundle',
      id: 'bundle_savings',
      isActive: isBundle || multiPolicyAvailable,
      canBeAdded: true,
      description:
        'This discount is available to applicants who purchase more than one policy with Branch. It increases the more policies the member has with us.',
      amount: calculateBundleSavings()
    };

    // bundle savings - before defining,
    // check discounts that have booleans/info (discountInventoryScore, discountPaperless) on quote.global
    // and default to null if not offered and not able to add them through the module
    // If able, then use the isActive flag to check
    // If not, check the canBeAdded flag to display it in the module

    if (bundleOptionAvailable || multiPolicyAvailable) {
      discounts.push(bundleSavings);
    }

    // MARK: Multiple Cars Discount
    const multipleCarCov = autoOption?.autoCoverages?.find((cov) => cov.type === 'ho_mh_mc_savings');

    const multipleCarsDiscount = {
      label: 'Multiple cars',
      id: 'ho_mh_mc_savings',
      isActive: multipleCarCov?.amount > 0,
      description: 'The applicant qualifies for a discount on the auto policy for having more than one vehicle.',
      amount: multipleCarCov?.amount
    };

    // Only want to display this discount if it's a monoline auto policy
    // Otherwise, this discount is included in the bundle savings
    if (multipleCarCov?.amount > 0 && selectedOption === policyTypes.Auto) {
      discounts.push(multipleCarsDiscount);
    }

    // MARK: Inventory Discount
    const inventoryFee = generateFee(FeeType.Imagery, {
      selectedOption,
      state: offer.quote.correctedAddress.state
    });

    // MARK: My Community Discount
    const myCommunitySavings = {
      label: 'My Community',
      id: 'my_community_discount_savings',
      description: 'The applicant qualifies for a discount because they have been referred by a current Branch member.'
    };

    // MARK: Inventory Discount
    const inventorySavings =
      homeOption && allowInventoryDiscount
        ? {
            label: 'Inventory',
            id: 'imagery_collection_savings',
            isActive: discountInventoryScore,
            isActiveOverride: store.includeInventory,
            possibleSavings: !homeOption
              ? 0
              : Math.round(
                  (inventoryFee.amount * 100) /
                    (offer.quote.isBix
                      ? homeOption?.homeTotal || 0
                      : (homeOption?.homeTotal || 0) + (autoOption?.autoTotal || 0))
                ),
            canBeAdded: true,
            description:
              'This dwelling policy discount is available for applicants who commit to submitting photos of their dwelling and any other covered items after joining Branch. Members have 15 days after purchase to submit their inventory in order to retain the discount.'
          }
        : null;

    // MARK: Paperless Discount
    const paperlessSavings = allowPaperlessDiscount
      ? {
          label: 'Paperless',
          id: 'paperless_savings',
          isActive: discountPaperless,
          isActiveOverride: store.includePaperless,
          possibleSavings: 4, // possible saving if not toggled
          canBeAdded: true,
          description:
            'This discount is available to applicants who agree to receive their ID cards and policy documents electronically, rather than receiving paper copies in their mail.'
        }
      : null;

    // MARK: Affinity Discount
    const affinitySavings = affinity
      ? {
          label: `Affinity - ${affinity} (Annual)`,
          id: 'affinity_discount_savings',
          isActive: true,
          description: `The applicant qualifies for a discount because they’ve been referred from ${offer.quote.leadSource}`
        }
      : null;

    // MARK: Homeowner Discount for auto only
    const homeownerSavings = {
      label: 'Homeowner discount',
      id: 'homeowner_discount_savings',
      description:
        'Homeowner discount on auto because the member owns either a home, condo, or manufactured/mobile home'
    };

    // MARK: Connected Home Discount
    const connectedHomeDescription = () => {
      return (
        <>
          This dwelling policy discount is available to applicants whose homes are professionally monitored by a home
          security system.
          <br />
          <br />
          After purchase, members have 15 days to submit proof of their home security provider to retain the discount.
        </>
      );
    };
    const connectedHomeSavings = {
      label: 'Connected Home',
      id: 'connected_home_discount_savings',
      isActive: store.includeConnectedHome,
      possibleSavings: 18, // possible saving if not added
      canBeAdded: true,
      canEdit: true,
      description: connectedHomeDescription()
    };

    // MARK: Windstorm Mitigation Discount
    const windstormMitigationLevel = selectedOption.includes(policyTypes.Home)
      ? offer.quote?.home?.windstormMitigationLevel
      : selectedOption.includes(policyTypes.Condo)
        ? offer.quote?.condo?.windstormMitigationLevel
        : '';
    const alWindstormMitigationDiscount = {
      label: 'Windstorm mitigation',
      id: 'windstorm_mitigation_discount_savings',
      description:
        'The applicant qualifies for a discount on the home policy because the home is retrofitted to withstand storms.',
      isActive: windstormMitigationLevel > 0
    };

    // MARK: GA Fortified Home Discount
    const fortifiedHomeDiscount = {
      label: 'Fortified Home',
      id: 'storm_shutter_savings', // this isn't a typo -- clariondoor is using the storm shutter field for fortified home in GA
      description:
        'The applicant qualifies for a discount on the home policy because the home is IBHS-certified as a Fortified Home, ensuring enhanced protection against severe weather.',
      isActive: store.state === 'GA' && offer.quote?.home?.fortifiedHome > 0
    };

    // MARK: Branch Plus Multi Policy Discount
    const branchPlusMultiPolicyDiscount = {
      label: 'Branch Plus Bundle',
      id: 'multi_policy_savings',
      isActive: includeBranchPlusMultiPolicyDiscount,
      canBeAdded: true,
      description:
        'A Branch Plus bundle discount can be applied if you bind a Home, Condo, Renters, and/or Personal Umbrella policy from a valid Branch Plus carrier partner.'
    };

    // MARK: Good Student Discount
    const rateControlDate = offer.quote.global?.rateControlDate;
    const goodStudentCanBeAdded =
      offer.quote.drivers.filter((d) =>
        canPersonBeFullTimeStudent({ driverOrPerson: d, driversOrPeople: offer.quote.drivers, rateControlDate })
      ).length > 0;

    const goodStudentDescription = () => {
      return (
        <>
          This auto policy discount is available to full-time student drivers under 23 who maintain good academic
          standing and are not the primary insured or spouse. Eligible drivers can opt-in or out of participation using
          the 'full-time student' toggle in the People tab.
          <br />
          <br />
          After purchase, members have 15 days to submit proof of academic standing to retain the discount. <br />
          <br />
          In addition, student drivers may receive an additional distant student discount if their school is more than X
          miles from the member's address
        </>
      );
    };
    const goodStudentDiscount = {
      label: 'Good Student',
      id: 'good_student',
      isActive: offer.quote.drivers.some((d) => d.goodStudent),
      canBeAdded: goodStudentCanBeAdded,
      possibleSavings: 10,
      description: goodStudentDescription()
    };

    // MARK: Driving Course
    const drivingCourseDescription = () => {
      return (
        <>
          This auto policy discount is available to drivers who voluntarily complete a state-approved accident
          prevention course (not court-ordered). The type of driving course eligible for discount will vary depending on
          the state and the driver’s attributes but eligible drivers will find a checkbox in the driver’s detail section
          of the People tab to opt in or out of this discount.
          <br />
          <br />
          After purchase, members have 15 days to submit proof of course completion to retain the discount. To maintain
          the discount at renewal, the driver must avoid at-fault accidents and more than one moving violation during
          the policy term.
          <br />
          <br />
          {isAgency
            ? 'For more details about our driving course discount, please refer to your Branch resources.'
            : 'For more details about our driving course discount, please contact support.'}
        </>
      );
    };
    const canReceiveDrivingCourseDiscount =
      offer.quote.drivers.filter((d) => {
        const {
          canReceiveDrivingCourseDiscount,
          canReceiveInexperiencedDrivingCourseDiscount,
          canReceiveAdditionalDrivingCourseDiscount
        } = canPersonReceiveDrivingCourseDiscount({
          driverOrPerson: d,
          state: offer.quote.correctedAddress.state || offer.state
        });
        return (
          canReceiveDrivingCourseDiscount ||
          canReceiveInexperiencedDrivingCourseDiscount ||
          canReceiveAdditionalDrivingCourseDiscount
        );
      }).length > 0;
    const drivingCourseDiscount = {
      label: 'Driving Course',
      id: 'driving_course',
      isActive: offer.quote.drivers.some((d) => d.courseDiscountConditionCheck || d.takenCourse),
      possibleSavings: 5,
      canBeAdded: canReceiveDrivingCourseDiscount,
      description: drivingCourseDescription()
    };

    // MARK: Community Drive
    const communityDriveDiscount = {
      label: (
        <span>
          Community Drive
          <br /> (Telematics)
        </span>
      ),
      id: 'community_drive',
      isActive: offer.quote.drivers.some((d) => d.everCommunityDriveParticipation || d.communityDriveParticipation),
      possibleSavings: 20,
      canEdit: true,
      canBeAdded: canReceiveCommunityDriveDiscount({
        state: offer.quote.correctedAddress.state,
        isBix: offer.quote.isBix
      }),
      appcuesId: '55cbcaec-b292-484b-9210-c6a0162f8b93'
    };

    function discountAdded(option, type) {
      return (
        option?.homeCoverages?.find((c) => c.type === type)?.amount > 0 ||
        option?.autoCoverages?.find((c) => c.type === type)?.amount > 0 ||
        option?.condoCoverages?.find((c) => c.type === type)?.amount > 0
      );
    }
    // MARK: New home discount
    const newHomeDiscountAdded = isBundle
      ? discountAdded(bundleOption, 'new_home_savings')
      : discountAdded(homeOption, 'new_home_savings');
    const newHomeDiscount = {
      label: 'New home',
      id: 'new_home_savings',
      isActive: newHomeDiscountAdded,
      possibleSavings: 0,
      canBeAdded: false,
      description:
        'The applicant qualifies for a discount on the dwelling policy because the dwelling has been built or purchased within the last 5 years.'
    };

    // MARK: Claim free discount
    const claimFreeDiscountAdded = isBundle
      ? discountAdded(bundleOption, 'claim_free_savings')
      : discountAdded(homeOption, 'claim_free_savings');
    const claimFreeDiscount = {
      label: 'Claim free',
      id: 'claim_free_savings',
      isActive: claimFreeDiscountAdded,
      possibleSavings: 0,
      canBeAdded: false,
      description:
        'The applicant qualifies for a discount on the dwelling policy because they have not filed a dwelling claim in the last 12 months. The applicant may receive a deeper discount the longer they have gone claim free, up to 5 years.'
    };

    // MARK: Three year safe driving
    const threeYearSafeDrivingAdded = isBundle
      ? discountAdded(bundleOption, 'three_year_savings')
      : discountAdded(autoOption, 'three_year_savings');
    const threeYearSafeDriving = {
      label: 'Three-year safe driving',
      id: 'three_year_savings',
      isActive: threeYearSafeDrivingAdded,
      possibleSavings: 0,
      canBeAdded: false,
      description:
        'The applicant qualifies for a discount on the auto policy because they have not been involved in an at-fault accident, not-at-fault accident, or received a major or minor driving ticket within the last 3 years.'
    };

    // MARK: Future effective
    const futureEffectiveDiscountAdded = isBundle
      ? discountAdded(bundleOption, 'future_effective_date_savings')
      : discountAdded(optionForDiscountCheck, 'future_effective_date_savings');
    const futureEffectiveDiscount = {
      label: 'Future effective',
      id: 'future_effective_date_savings',
      isActive: futureEffectiveDiscountAdded,
      possibleSavings: 0,
      canBeAdded: false,
      description:
        'The applicant qualifies for a discount on the auto policy, home policy or both because the effective date is at least 3 days away. This discount is based on a bind date of today and may change if the offer is bound closer to the effective date. The amount varies by state and increases if the effective date is 8 or more days in the future.'
    };

    // MARK: Smart tech savings
    const smartTechSavingsAdded = isBundle
      ? discountAdded(bundleOption, 'smart_tech_savings')
      : discountAdded(autoOption, 'smart_tech_savings');
    const smartTechSavings = {
      label: 'Smart technology',
      id: 'smart_tech_savings',
      isActive: smartTechSavingsAdded,
      possibleSavings: 0,
      canBeAdded: false,
      description:
        'The applicant qualifies for a discount on the auto policy because one or more vehicles are equipped with factory-installed blind spot warning or automatic emergency braking.'
    };

    // MARK: Preferred payer
    const preferredPayerAdded = isBundle
      ? discountAdded(bundleOption, 'preferred_payer_savings')
      : discountAdded(homeOption || condoOption, 'preferred_payer_savings');
    const preferredPayer = {
      label: 'Preferred payer',
      id: 'preferred_payer_savings',
      isActive: preferredPayerAdded,
      possibleSavings: 0,
      canBeAdded: false,
      description: isAgency
        ? 'The applicant qualifies for a discount on the dwelling policy due to the number of mortgages, the payment method, and/or the payment history. For more details about our preferred payer savings, please contact support.'
        : 'The applicant qualifies for a discount on the dwelling policy due to the number of mortgages, the payment method, and/or the payment history. For more details about our preferred payer savings, please refer to your Branch resources.'
    };

    // MARK: Good Payer
    const goodPayerAdded = isBundle
      ? discountAdded(bundleOption, 'good_payer_savings')
      : discountAdded(autoOption, 'good_payer_savings');
    const goodPayer = {
      label: 'Good payer',
      id: 'good_payer_savings',
      isActive: goodPayerAdded,
      possibleSavings: 0,
      canBeAdded: false,
      description: isAgency
        ? 'The applicant qualifies for a discount on the auto policy due to their payment history. This discount applies to all new auto policies but will be removed if a non-payment cancellation notice is issued. For more details about our good payer savings, please contact support.'
        : 'The applicant qualifies for a discount on the auto policy due to their payment history. This discount applies to all new auto policies but will be removed if a non-payment cancellation notice is issued. For more details about our good payer savings, please refer to your Branch resources.'
    };

    // MARK: Continuous insurance
    const continuousInsuranceAdded = isBundle
      ? discountAdded(bundleOption, 'continuous_savings')
      : discountAdded(autoOption, 'continuous_savings');
    const continuousInsurance = {
      label: 'Continuous insurance',
      id: 'continuous_savings',
      isActive: continuousInsuranceAdded,
      possibleSavings: 0,
      canBeAdded: false,
      description:
        'The applicant qualifies for a discount on the auto policy because they’ve had continuous auto insurance for the last 3 years prior to joining Branch.'
    };

    // MARK: Discounts per policy type
    const allPoliciesDiscounts = [myCommunitySavings, paperlessSavings, affinitySavings, futureEffectiveDiscount];
    const homeDiscounts = [
      connectedHomeSavings,
      inventorySavings,
      alWindstormMitigationDiscount,
      newHomeDiscount,
      claimFreeDiscount,
      preferredPayer,
      fortifiedHomeDiscount
    ];
    const rentersDiscounts = [connectedHomeSavings];
    const condoDiscounts = [{ ...connectedHomeSavings, possibleSavings: 6.6 }, claimFreeDiscount, preferredPayer];
    const autoDiscounts = [
      homeownerSavings,
      communityDriveDiscount,
      goodStudentDiscount,
      drivingCourseDiscount,
      threeYearSafeDriving,
      smartTechSavings,
      goodPayer,
      continuousInsurance
    ];
    if (statesEligibleForBranchPlusMPD[store.state]?.[selectedOption]) {
      autoDiscounts.push(branchPlusMultiPolicyDiscount);
    }

    // MARK: All Policies Discounts
    allPoliciesDiscounts.forEach((discount) => {
      if (discount) {
        let total = 0;
        if (selectedOption.includes(policyTypes.Auto)) {
          const coverages = isBundle && bundleOption ? bundleOption.autoCoverages : autoOption?.autoCoverages;
          total += getCoveragesDiscount(coverages, discount.id);
        }
        if (selectedOption.includes(policyTypes.Home)) {
          const coverages = isBundle && bundleOption ? bundleOption.homeCoverages : homeOption?.homeCoverages;
          total += getCoveragesDiscount(coverages, discount.id);
        }
        if (selectedOption.includes(policyTypes.Condo)) {
          const coverages = isBundle && bundleOption ? bundleOption.condoCoverages : condoOption?.condoCoverages;
          total += getCoveragesDiscount(coverages, discount.id);
        }

        if (selectedOption.includes(policyTypes.Renters) && standaloneRentersStates[store.state]) {
          const coverages = isBundle && bundleOption ? bundleOption.rentersCoverages : rentersOption?.rentersCoverages;
          total += getCoveragesDiscount(coverages, discount.id);
        }

        // MARK: use when coverages with same discount ID need to be added together for one displayed discount amount
        if (summedPolicyDiscountIDs.includes(discount.id) && isBundle && bundleOption) {
          const autoCoverages = bundleOption.autoCoverages;
          const homeCoverages = bundleOption.homeCoverages;
          const coverages = [...(autoCoverages || []), ...(homeCoverages || [])];
          total = getCoveragesDiscount(coverages, discount.id);
        }

        if (typeof discount.isActiveOverride === 'boolean') {
          discount.isActive = discount.isActiveOverride;
        } else {
          discount.isActive = total > 0;
        }
        discount.amount = total;
        (discount.canBeAdded || discount.isActive) && discounts.push(discount);
      }
    });

    // MARK: Home Discounts
    selectedOption.includes('H') &&
      homeDiscounts.forEach((discount) => {
        if (discount) {
          let total = 0;
          total += getCoveragesDiscount(
            isBundle && bundleOption ? bundleOption.homeCoverages : homeOption?.homeCoverages,
            discount.id
          );
          discount.isActive =
            typeof discount.isActiveOverride === 'boolean' ? discount.isActiveOverride : total > 0 || discount.isActive;
          discount.amount = total;

          if (discount.canBeAdded || discount.isActive) {
            discounts.push(discount);
          }
        }
      });

    // MARK: Auto Discounts
    selectedOption.includes(policyTypes.Auto) &&
      autoDiscounts.forEach((discount) => {
        if (discount) {
          let total = 0;
          total += getCoveragesDiscount(
            isBundle && bundleOption ? bundleOption.autoCoverages : autoOption?.autoCoverages,
            discount.id
          );
          discount.isActive =
            typeof discount.isActiveOverride === 'boolean' ? discount.isActiveOverride : total > 0 || discount.isActive;
          discount.amount = total;

          if (discount.canBeAdded || discount.isActive) {
            discounts.push(discount);
          }
        }
      });

    // MARK: Condo Discounts
    if (selectedOption.includes(policyTypes.Condo)) {
      condoDiscounts.forEach((discount) => {
        if (discount) {
          const total = getCoveragesDiscount(
            isBundle && bundleOption ? bundleOption.condoCoverages : condoOption?.condoCoverages,
            discount.id
          );
          discount.isActive =
            typeof discount.isActiveOverride === 'boolean' ? discount.isActiveOverride : total > 0 || discount.isActive;
          discount.amount = total;

          if (discount.canBeAdded || discount.isActive) {
            discounts.push(discount);
          }
        }
      });
    }

    // MARK: Renters Discounts
    if (selectedOption.includes(policyTypes.Renters) && standaloneRentersStates[store.state]) {
      rentersDiscounts.forEach((discount) => {
        let total = 0;
        total += getCoveragesDiscount(
          isBundle && bundleOption ? bundleOption.rentersCoverages : rentersOption?.rentersCoverages,
          discount.id
        );
        discount.isActive =
          typeof discount.isActiveOverride === 'boolean' ? discount.isActiveOverride : !!total || discount.isActive;
        discount.amount = total;

        if (discount.canBeAdded || discount.isActive) {
          discounts.push(discount);
        }
      });
    }

    return discounts.sort((a, b) => {
      // Always keep Bundle at the top
      if (a.id === 'bundle_savings') {
        return -1;
      }
      if (b.id === 'bundle_savings') {
        return 1;
      }

      // Place Community Drive right after Bundle
      if (a.id === 'community_drive') {
        return -1;
      }
      if (b.id === 'community_drive') {
        return 1;
      }

      // Sort the rest by amount
      return b.amount - a.amount;
    });
  }, [
    offer,
    selectedOption,
    store.state,
    store.includeConnectedHome,
    store.includeInventory,
    store.includePaperless,
    allowPaperlessDiscount,
    allowInventoryDiscount,
    includeBranchPlusMultiPolicyDiscount,
    isAgency
  ]);
};
